import { ReactElement } from 'react';
import { AppTo } from '../../../routes';
import { tokens } from '../../theme';
import { AppLink } from '../AppLink/AppLink';

type ButtonVariant = 'primary' | 'secondary';

type ButtonBaseProps = {
  children: string;
  variant: ButtonVariant;
  expanded?: boolean;
  disabled?: boolean;
};

type ActionButtonProps = ButtonBaseProps & {
  to?: undefined;
  onClick: () => void;
};

type NavigationButtonProps = ButtonBaseProps & {
  to: AppTo;
  onClick?: undefined;
};

type ButtonProps = ActionButtonProps | NavigationButtonProps;

/**
 * Botão de ação (`onClick`) ou de navegação (`to`) — navegação usa `AppLink` por baixo dos panos,
 * preservando a semântica de link exigida pelas convenções de acessibilidade do projeto.
 */
export const Button = (props: ButtonProps): ReactElement => {
  if (props.to !== undefined) {
    const disabled = Boolean(props.disabled);
    const style = { ...BASE_STYLE, ...variantStyle(props.variant, disabled) };
    return (
      <AppLink
        to={props.to}
        aria-disabled={disabled || undefined}
        style={{ ...style, width: props.expanded ? '100%' : 'auto', margin: 'auto' }}
      >
        {props.children}
      </AppLink>
    );
  }

  const style = { ...BASE_STYLE, ...variantStyle(props.variant, Boolean(props.disabled)) };
  return (
    <button
      type="button"
      disabled={props.disabled}
      onClick={props.onClick}
      style={{ ...style, width: props.expanded ? '100%' : 'auto', margin: 'auto' }}
    >
      {props.children}
    </button>
  );
};

function variantStyle(variant: ButtonVariant, disabled: boolean): React.CSSProperties {
  const enabledColor = tokens.colors.brand.green.DEFAULT;
  const disabledColor = tokens.colors.brand.gray.light650;

  const color = disabled ? disabledColor : enabledColor;
  const fontColor = variant === 'secondary' ? enabledColor : tokens.colors.global.white;

  return {
    backgroundColor: variant === 'secondary' ? 'transparent' : color,
    border: `1px solid ${color}`,
    color: fontColor,
    cursor: disabled ? 'not-allowed' : 'pointer',
  };
}

const BASE_STYLE: React.CSSProperties = {
  flex: 1,
  height: tokens.dimensions.controlHeight,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: `${tokens.spacing.small} ${tokens.spacing.default}`,
  borderRadius: tokens.radius.medium,
  fontSize: tokens.typography.fontSize.button,
  fontWeight: tokens.typography.fontWeight.medium,
  fontFamily: 'inherit',
  boxSizing: 'border-box',
  textDecoration: 'none',
};
